import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Checkbox,
  FormGroup,
  Tooltip,
  IconButton,
  Modal,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays } from 'date-fns';
import { UserAddress } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';
import TrustedContactSelector from './TrustedContactSelector';
import { useAuth } from '../context/AuthContext';
import { CONTRACT_TEXT } from '../utils/constants';

const steps = ['Delivery', 'Customer Contact', 'Billing & Payment', 'Summary'];
const pickupLocation = "69 West 3rd Ave, Vancouver BC, V5Y3T8.";
type DeliveryWindow = 'before_9am' | '9am_1pm' | '1pm_5pm' | 'after_5pm' | '';

const deliveryWindowOptions: Array<{ value: DeliveryWindow; label: string }> = [
  { value: '', label: 'No time selected' },
  { value: 'before_9am', label: 'Before 9am' },
  { value: '9am_1pm', label: '9am-1pm' },
  { value: '1pm_5pm', label: '1pm-5pm' },
  { value: 'after_5pm', label: 'After 5pm' },
];

interface CheckoutStepsProps {
  activeStep: number;
  addresses: UserAddress[];
  preapprovedShipping: boolean;
  deliveryOption: 'delivery' | 'pickup';
  selectedShippingAddress: number | '';
  deliveryDateTime: Date | null;
  deliveryTimeWindow: DeliveryWindow;
  returnPickupDateTime: Date | null;  // Add return pickup date
  returnPickupTimeWindow: DeliveryWindow;
  selectedTrustedContact: number | '';
  onTrustedContactChange: (contactId: number) => void;
  useShippingForBilling: boolean;
  selectedBillingAddress: number | '';
  notes: string;
  onDeliveryOptionChange: (option: 'delivery' | 'pickup') => void;
  onShippingAddressChange: (addressId: number) => void;
  onDeliveryDateTimeChange: (date: Date | null) => void;
  onDeliveryDateTimeAccept: (date: Date | null) => void;
  onDeliveryTimeWindowChange: (window: DeliveryWindow) => void;
  onReturnPickupDateTimeChange: (date: Date | null) => void;  // Add handler for return pickup date
  onReturnPickupTimeWindowChange: (window: DeliveryWindow) => void;
  onUseShippingForBillingChange: (use: boolean) => void;
  onBillingAddressChange: (addressId: number) => void;
  onNotesChange: (notes: string) => void;
  onAddNewAddress: () => void;
  handleBack: () => void;
  handleNext: () => void;
  paymentMethod: string | null;
  onPaymentMethodChange: (method: string) => void;
  loadingShipping: boolean;
  returnOption: 'pickup' | 'self_return' | 'with_next_delivery';
  onReturnOptionChange: (option: 'pickup' | 'self_return' | 'with_next_delivery') => void;
  contractAccepted: boolean;
  onContractAcceptedChange: (accepted: boolean) => void;
  contactPreference: boolean;
  onContactPreferenceChange: (preference: boolean) => void;
}

const CheckoutSteps: React.FC<CheckoutStepsProps> = (props) => {
  const {
    activeStep,
    addresses,
    deliveryOption,
    selectedShippingAddress,
    deliveryDateTime, // Changed prop name here
    deliveryTimeWindow,
    returnPickupDateTime,  // Add this
    returnPickupTimeWindow,
    selectedTrustedContact,
    onTrustedContactChange,
    useShippingForBilling,
    selectedBillingAddress,
    notes,
    onDeliveryOptionChange,
    onShippingAddressChange,
    onDeliveryDateTimeChange, // Updated prop name
    onDeliveryDateTimeAccept,
    onDeliveryTimeWindowChange,
    onReturnPickupDateTimeChange,  // Add this
    onReturnPickupTimeWindowChange,
    onUseShippingForBillingChange,
    onBillingAddressChange,
    onNotesChange,
    onAddNewAddress,
    paymentMethod,
    onPaymentMethodChange,
    loadingShipping,
    returnOption,
    onReturnOptionChange,
    contractAccepted,
    onContractAcceptedChange,
    contactPreference,
    onContactPreferenceChange,
    preapprovedShipping,
  } = props;

  const [contractModalOpen, setContractModalOpen] = useState(false);

  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const shouldDisableDate = (date: Date) => {
    const isApprovedForNextDay = user?.approved_for_next_day_delivery;

    if (isApprovedForNextDay) {
      // Approved users can order starting tomorrow (next-day delivery)
      const tomorrow = addDays(new Date(), 1);
      tomorrow.setHours(0, 0, 0, 0);
      return date < tomorrow;
    } else {
      // Non-approved users must wait 7 days (date only)
      const sevenDaysFromNow = addDays(new Date(), 6);
      sevenDaysFromNow.setHours(0, 0, 0, 0); // Set to midnight for date comparison
      return date < sevenDaysFromNow;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // Delivery
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Delivery Option</Typography>
            <RadioGroup row value={deliveryOption} onChange={(e) => onDeliveryOptionChange(e.target.value as 'delivery' | 'pickup')}>
              <FormControlLabel value="delivery" control={<Radio />} label="Delivery" />
              <FormControlLabel value="pickup" control={<Radio />} label="Self Pick Up" />
            </RadioGroup>

            {deliveryOption === 'delivery' ? (
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="shipping-address-label">Shipping Address</InputLabel>
                  <Select
                    labelId="shipping-address-label"
                    value={selectedShippingAddress}
                    onChange={(e) => {
                      const value = e.target.value as string | number;
                      if (value === 'add_new') {
                        onAddNewAddress();
                      } else {
                        onShippingAddressChange(value as number);
                      }
                    }}
                    label="Shipping Address"
                  >
                    {addresses.map((address) => (
                      <MenuItem key={address.id} value={address.id}>
                        <Box>
                          <Typography variant="body1">{address.address_line_1}</Typography>
                          <Typography variant="body2" color="text.secondary">{`${address.city}, ${address.state} ${address.postal_code}`}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                    {(addresses.length === 0 || addresses.length > 1) && (
                      <MenuItem value="add_new">
                        <Typography variant="body1" color="primary">+ Add New Address</Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                <DatePicker
                  label="Delivery Date"
                  value={deliveryDateTime}
                  onChange={onDeliveryDateTimeChange}
                  onAccept={onDeliveryDateTimeAccept}
                  minDate={user?.approved_for_next_day_delivery ? addDays(new Date(), 1) : addDays(new Date(), 6)}
                  shouldDisableDate={shouldDisableDate}
                  slotProps={{ textField: { required: true } }}
                  sx={{ mt: 2, width: '100%' }}
                />
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="delivery-window-label">Delivery Time Window</InputLabel>
                  <Select
                    labelId="delivery-window-label"
                    value={deliveryTimeWindow}
                    label="Delivery Time Window"
                    onChange={(e) => onDeliveryTimeWindowChange(e.target.value as DeliveryWindow)}
                  >
                    {deliveryWindowOptions.map((option) => (
                      <MenuItem key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {user?.approved_for_next_day_delivery 
                    ? "Next-day delivery available. For same-day delivery, add your request in the order notes below." 
                    : "For same-day or next-day delivery, add your request in the order notes below and we'll try to accommodate."}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Pickup Location:</Typography>
                <Typography>{pickupLocation}</Typography>
                <DatePicker
                  label="Pickup Date"
                  value={deliveryDateTime}
                  onChange={onDeliveryDateTimeChange}
                  onAccept={onDeliveryDateTimeAccept}
                  minDate={user?.approved_for_next_day_delivery ? addDays(new Date(), 1) : addDays(new Date(), 6)}
                  shouldDisableDate={shouldDisableDate}
                  sx={{ mt: 2, width: '100%' }}
                />
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="pickup-window-label">Pickup Time Window</InputLabel>
                  <Select
                    labelId="pickup-window-label"
                    value={deliveryTimeWindow}
                    label="Pickup Time Window"
                    onChange={(e) => onDeliveryTimeWindowChange(e.target.value as DeliveryWindow)}
                  >
                    {deliveryWindowOptions.map((option) => (
                      <MenuItem key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {user?.approved_for_next_day_delivery 
                    ? "Next-day pickup available. For same-day pickup, add your request in the order notes below." 
                    : "For same-day or next-day pickup, add your request in the order notes below and we'll try to accommodate."}
                </Typography>
              </Box>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Return Option</Typography>
            <RadioGroup row value={returnOption} onChange={(e) => onReturnOptionChange(e.target.value as 'pickup' | 'self_return' | 'with_next_delivery')}>
              <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
              <FormControlLabel value="self_return" control={<Radio />} label="Self Return" />
              <FormControlLabel value="with_next_delivery" control={<Radio />} label="With Next Delivery" />
            </RadioGroup>

            {(returnOption === 'pickup' || returnOption === 'self_return') && (
              <>
                <DatePicker
                label={returnOption === 'pickup' ? "Return Pickup Date" : "Self Drop-off Date"}
                value={returnPickupDateTime}
                onChange={onReturnPickupDateTimeChange}
                minDate={addDays(new Date(), 1)}
                shouldDisableDate={shouldDisableDate}
                sx={{ mt: 2, width: '100%' }}
              />
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="return-window-label">
                    {returnOption === 'pickup' ? 'Return Pickup Time Window' : 'Self Drop-off Time Window'}
                  </InputLabel>
                  <Select
                    labelId="return-window-label"
                    value={returnPickupTimeWindow}
                    label={returnOption === 'pickup' ? 'Return Pickup Time Window' : 'Self Drop-off Time Window'}
                    onChange={(e) => onReturnPickupTimeWindowChange(e.target.value as DeliveryWindow)}
                  >
                    {deliveryWindowOptions.map((option) => (
                      <MenuItem key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        );
      case 1: // Trusted Contact
        return (
          <Box sx={{ mt: 3 }}>
            <TrustedContactSelector
              selectedContactId={selectedTrustedContact}
              onContactChange={onTrustedContactChange}
              onAddNewContact={() => {}}
            />
            
            {selectedTrustedContact && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={contactPreference}
                      onChange={(e) => onContactPreferenceChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="I would like to be contacted regarding this order"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  If checked, we may contact your selected customer contact about order updates, delivery scheduling, or any issues that arise.
                </Typography>
              </Box>
            )}
          </Box>
        );
      case 2: // Billing & Payment
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Billing Information</Typography>
            {deliveryOption === 'delivery' && (
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={useShippingForBilling} onChange={(e) => onUseShippingForBillingChange(e.target.checked)} />}
                  label="My billing address is the same as my shipping address."
                />
              </FormGroup>
            )}
            {(deliveryOption === 'pickup' || !useShippingForBilling) && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="billing-address-label">Billing Address</InputLabel>
                <Select
                  labelId="billing-address-label"
                  value={selectedBillingAddress}
                  onChange={(e) => {
                    const value = e.target.value as string | number;
                    if (value === 'add_new') {
                      onAddNewAddress();
                    } else {
                      onBillingAddressChange(value as number);
                    }
                  }}
                  label="Billing Address"
                >
                  {addresses.map((address) => (
                    <MenuItem key={address.id} value={address.id}>
                      <Box>
                        <Typography variant="body1">{address.address_line_1}</Typography>
                        <Typography variant="body2" color="text.secondary">{`${address.city}, ${address.state} ${address.postal_code}`}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                  {(addresses.length === 0 || addresses.length > 1) && (
                    <MenuItem value="add_new">
                      <Typography variant="body1" color="primary">+ Add New Address</Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Payment Method</Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="payment-method-label">Payment Method</InputLabel>
              <Select
                labelId="payment-method-label"
                value={paymentMethod || ''}
                label="Payment Method"
                onChange={(e) => onPaymentMethodChange(e.target.value as string)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={selected === 'e-transfer' ? 'money-bill-transfer' : 
                                          selected === 'EFT' ? 'building-columns' : 
                                          selected === 'cheque' ? 'money-check' : 
                                          selected === 'credit card (+2.9% fee)' ? 'credit-card' :
                                          selected === 'other' ? 'ellipsis-h' : 'question-circle'} style={{ marginRight: '8px' }} />
                    {selected}
                  </Box>
                )}
              >
                <MenuItem value="e-transfer"><FontAwesomeIcon icon="money-bill-transfer" style={{ marginRight: '8px' }} /> e-transfer</MenuItem>
                <MenuItem value="EFT"><FontAwesomeIcon icon="building-columns" style={{ marginRight: '8px' }} /> EFT</MenuItem>
                <MenuItem value="cheque"><FontAwesomeIcon icon="money-check" style={{ marginRight: '8px' }} /> cheque</MenuItem>
                <MenuItem value="credit card (+2.9% fee)"><FontAwesomeIcon icon="credit-card" style={{ marginRight: '8px' }} /> credit card (+2.9% fee)</MenuItem>
                <MenuItem value="other"><FontAwesomeIcon icon="ellipsis-h" style={{ marginRight: '8px' }} /> other</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      case 3: // Summary
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Order Summary</Typography>
            <Typography>Review your order details on the right.</Typography>
            <TextField
              label="Add a note to your order"
              multiline
              rows={3}
              fullWidth
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              sx={{ mt: 2 }}
            />

            {/* Bins collection note with info bubble */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Have you added bins to collect used items?
                </Typography>
                <Tooltip 
                  title="# of bars, concessions, or points of sale where reusables will be distributed + # of waste stations (clusters of bins for landfill, compost, etc.) + # of exits"
                  placement="top"
                  arrow
                >
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <Info fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Customer Agreement */}
            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                Please review and accept the ShareWares Customer Agreement before submitting your quote for approval.
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={contractAccepted} 
                      onChange={(e) => onContractAcceptedChange(e.target.checked)}
                    />
                  }
                  label={
                    <Typography>
                      I have read and agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setContractModalOpen(true)}
                        style={{
                          color: 'inherit',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          font: 'inherit',
                        }}
                      >
                        ShareWares Customer Agreement
                      </button>
                    </Typography>
                  }
                />
              </FormGroup>
            </Box>
          </Box>
        );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Box>
      <Stepper 
        activeStep={activeStep} 
        sx={{ my: 3 }}
        orientation={isMobile ? 'vertical' : 'horizontal'}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel 
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: { xs: '0.75rem', sm: '1rem' },
                }
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <>
        {renderStepContent(activeStep)}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button onClick={props.handleBack}>
            Back
          </Button>
          <Button variant="outlined" onClick={() => {
            // Validate step 0 (Delivery) before proceeding
            if (activeStep === 0) {
              if (props.deliveryOption === 'delivery' && !props.deliveryDateTime) {
                toast.error("Please select a delivery date.");
                return;
              }
              if ((props.returnOption === 'pickup' || props.returnOption === 'self_return') && !props.returnPickupDateTime) {
                toast.error(props.returnOption === 'pickup' ? "Please select a return pickup date & time." : "Please select a self drop-off date & time.");
                return;
              }
            }
            // Validate step 1 (Trusted Contact) before proceeding
            if (activeStep === 1) {
              if (!selectedTrustedContact) {
                toast.error("Please select a customer contact.");
                return;
              }
            }
            props.handleNext();
          }}
          disabled={loadingShipping || (activeStep === steps.length - 1 && !contractAccepted)}
          >
            {activeStep === steps.length - 1
              ? preapprovedShipping
                ? 'Submit'
                : 'Submit Order Request'
              : 'Next'}
          </Button>
        </Box>
      </>

      {/* Contract Modal */}
      <Modal 
        open={contractModalOpen} 
        onClose={() => setContractModalOpen(false)}
        aria-labelledby="contract-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '70%' },
          maxWidth: 800,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography id="contract-modal-title" variant="h6" component="h2">
              ShareWares Customer Agreement
            </Typography>
          </Box>
          <Box sx={{ 
            p: 3, 
            maxHeight: 'calc(90vh - 150px)', // Adjusted from 120px to 150px
            overflow: 'auto',
            paddingBottom: '80px', // Add padding to ensure button is not cut off
            '& h2, & h3, & h4': { 
              fontWeight: 'bold',
              mt: 2,
              mb: 1
            },
            '& p': {
              mb: 1
            },
            '& ul': {
              mb: 1,
              pl: 2
            }
          }}>
            <Typography 
              component="div" 
              sx={{ 
                whiteSpace: 'pre-line',
                '& strong': { fontWeight: 'bold' }
              }}
              dangerouslySetInnerHTML={{ 
                __html: CONTRACT_TEXT
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/^• /gm, '• ')
              }}
            />
          </Box>
          <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}>
            <Button onClick={() => setContractModalOpen(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CheckoutSteps; 